import { ISizePosition } from '@/interfaces/general/ISizePosition.ts';
import { IDateRangeByNumberWithType } from '@/interfaces/general/IDateRangeByString.ts';
import { PeriodTypeOverridesWithInherited } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';
import type { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { aggregateType } from './series/ISeriesOption.ts';
import { IDateRangeCompositeNumbers } from '../general/IDateRange.ts';
import {
    IAxisLimits, IAxisPosition, ICombinedAggregateOptions, ViewMode,
} from './charts/IModuleChartConfig.ts';
import { IDateObject, IDateObjectBasic } from './IDateObject.ts';
import { ITableFilters } from './table/ITableFilters.ts';
import type { IReportMeta } from '../reports/IReportExport.ts';
import type { IOutputChartListItem, MarketChartsCategories } from '../storageForecasting/IRunOutput.ts';
import type { IAssetRunFilters } from '../storageForecasting/IAssetRunFilters.ts';
import type { IDashboardCommonSettings } from './dashboard/IDashboard.ts';

interface ILiveTableDataBase {
    country: string;
    moduleType: string;
}

export enum ModuleType {
    Chart = 'Chart',
    ForecastHistory = 'ForecastHistory',
    LiveTable = 'LiveTable',
    BoaDashboard = 'BoaDashboard',
    Metric = 'Metric',
    Note = 'Note',
    AncillaryChart = 'AncillaryChart',
    Treemap = 'Treemap',
    NivEvolution = 'NivEvolution',
    ReportPdfPage = 'ReportPdfPage',
    IndexGroup = 'IndexGroup',
    Leaderboard = 'Leaderboard',
    StorageForecastChart = 'StorageForecastChart',
}

export enum ModuleSize {
    ExtraSmall = 'ExtraSmall',
    Small = 'Small',
    SmallMedium = 'SmallMedium',
    SmallSmallMedium = 'SmallSmallMedium',
    Medium = 'Medium',
    MediumLarge = 'MediumLarge',
    LargeMedium = 'LargeMedium',
    Large = 'Large',
    ExtraLarge = 'ExtraLarge',
    LargeMediumLarge = 'LargeMediumLarge',
}

export interface INoteModule {
    data: {
        text: string;
    };
    info: {
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.Note;
    };
    reportMeta?: IReportMeta;
}

export interface IReportPdfPageModule {
    data: {
        documentShortCode: string;
        documentTitle: string;
        currentPageIndex?: number;
        pageIndex?: number;
        pageIndexes?: Array<number>;
        selectionMode?: 'WholeReport' | 'Pages';
        description?: string | null;
        publicationDate?: string | null;
        numberOfPages?: number | null;
        thumbnailUrl?: string | null;
    };
    info: {
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.ReportPdfPage;
    };
    reportMeta?: IReportMeta;
}

export type TIndexDisplayMode = 'table' | 'chart';
export type TIndexScope = 'group' | 'single';
export type TLeaderboardDisplayMode = 'table' | 'chart';
export type TStorageForecastAssetReportScope = 'comparison' | 'single';

export interface IIndexModuleDate extends Omit<IDateObjectBasic, 'aggregateOptions'> {
    aggregateOptions?: {
        granularity?: 'Day' | 'Week' | 'Month' | 'Year' | null;
    } | null;
}

export interface IIndexGroupModule {
    data: {
        indexGroupId: string;
        displayMode: TIndexDisplayMode;
        indexScope: TIndexScope;
        indexName?: string;
        indexId?: string;
        overrides?: IDashboardCommonSettings;
        date: IIndexModuleDate;
    };
    info: {
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.IndexGroup;
    };
    reportMeta?: IReportMeta;
}

export interface ILeaderboardModule {
    data: {
        leaderboardGroupId: string;
        displayMode: TLeaderboardDisplayMode;
        date: Omit<IDateObjectBasic, 'aggregateOptions'>;
    };
    info: {
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.Leaderboard;
    };
    reportMeta?: IReportMeta;
}

export interface IStorageForecastChartConfigBase {
    storageForecastConfigVersion: 2;
    title: string;
    sourceType: 'RunOutput' | 'SystemAssumptions' | 'SystemForecasts';
    sourceLabel: string;
}

export interface IStorageForecastAssetComparisonReportData extends IStorageForecastChartConfigBase {
    sourceType: 'RunOutput';
    assetScope: 'comparison';
    assetGroupId: string;
    assetRunFilters: IAssetRunFilters;
}

export interface IStorageForecastIndividualAssetReportData extends IStorageForecastChartConfigBase {
    sourceType: 'RunOutput';
    assetScope: 'single';
    assetGroupId: string;
    assetId: string;
    runId: string;
    assetRunFilters: IAssetRunFilters;
    outputChart: IOutputChartListItem;
}

interface IStorageForecastMarketChartConfigBase extends IStorageForecastChartConfigBase {
    country: TStorageForecastCountries;
    quarter: string;
    scenarioId: string;
    scenarioName: string;
    chartName: string;
}

export interface IStorageForecastMarketAssumptionsReportData extends IStorageForecastMarketChartConfigBase {
    sourceType: 'SystemAssumptions';
    marketChartCategory: MarketChartsCategories.SystemAssumptions;
}

export interface IStorageForecastSystemForecastReportData extends IStorageForecastMarketChartConfigBase {
    sourceType: 'SystemForecasts';
    marketChartCategory: MarketChartsCategories.SystemForecasts;
}

export type TStorageForecastChartData =
    | IStorageForecastAssetComparisonReportData
    | IStorageForecastIndividualAssetReportData
    | IStorageForecastMarketAssumptionsReportData
    | IStorageForecastSystemForecastReportData;

export interface IStorageForecastChartModule {
    data: TStorageForecastChartData;
    info: {
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.StorageForecastChart;
    };
    reportMeta?: IReportMeta;
}

export enum BoaIds {
    Boa = 'Boa',
    BodBid = 'BodBid',
    BodOffer = 'BodOffer',
    'BOA Metrics' = 'BOA Metrics',
    'BOA Stack' = 'BOA Stack',
    'BOA Map' = 'BOA Map',
    'Constraints Map' = 'Constraints Map',
    BalancingCostsGrid = 'BalancingCostsGrid',
    BoaLeaderboard = 'BoaLeaderboard',
    NivEvolution = 'NivEvolution',
}
export enum BoaTypes {
    LiveTable = 'LiveTable',
    BoaDashboard = 'BoaDashboard',
    BalancingCosts = 'BalancingCosts',
    BoaLeaderboard = 'BoaLeaderboard',
    NivEvolution = 'NivEvolution',
}
export interface IBoaDashboardModule {
    data: {
        moduleName: BoaIds;
        sorting?: {
            metric: string;
            order: string;
        };
    };
    info: {
        id: string;
        type: BoaTypes;
        sizeAndPosition: ISizePosition;
        dateOverride?: PeriodTypeOverridesWithInherited;
    };
    reportMeta?: IReportMeta;
}

export interface IBoaLeaderboardModule {
    info: {
        id: string;
        type: BoaTypes.BoaLeaderboard;
        sizeAndPosition: ISizePosition;
    };
    data: {
        type: BoaIds;
    };
    reportMeta?: IReportMeta;
}

export interface ILiveTableModule {
    data: ILiveTableDataBase & {
        date?: IDateObject;
    };
    info: {
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.LiveTable;
        dateOverride?: PeriodTypeOverridesWithInherited; // only used for boa live tables
    };
    presetFilters?: ITableFilters | null;
    reportMeta?: IReportMeta;
}

export interface IForecastHistoryModuleData {
    country: string;
    name?: string;
    optionsId: null | string;
    seriesId: string;
    showDeltaChart?: boolean;
    dateFormat?: IDateObject;
    daysBefore?: number;
    daysAfter?: number;
}

export interface IForecastHistoryModule {
    data: IForecastHistoryModuleData;
    info: {
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.ForecastHistory;
    };
    reportMeta?: IReportMeta;
}

export interface IMetricModule {
    dates: IDateRangeByNumberWithType;
    id: string;
    metricNames: {
        metricName: string;
        metricType: string | null;
        seriesOption: {
            country: string;
            isPlant: boolean;
            name: string;
            optionsId: string | null;
            seriesId: string;
            optionsIdArray?: Array<string>;
        };
    };
    sizeAndPosition: ISizePosition;
    reportMeta?: IReportMeta;
}

// legacy module. keeping for old dashboards compatibility
export interface IAncillaryModule {
    info: {
        ancillary: string;
        id: string;
        sizeAndPosition: ISizePosition;
        type: ModuleType.Metric;
    };
    reportMeta?: IReportMeta;
}

export interface IAncillaryChartModule {
    info: {
        id: string;
        type: ModuleType.AncillaryChart;
        sizeAndPosition: ISizePosition;
    };
    data: {
        seriesId: string;
        optionsId: string | null;
        type: string;
        date?: IDateObject;
    };
    reportMeta?: IReportMeta;
}

export interface IChartSeriesAdditionalDetails {
    dashStyle: string;
    dataLabelLocation: string;
    lineWidth: null | number;
    numberOfSeriesHof: number | null;
    opacity: number;
    prefix: null | string;
    stackGroup: null | number;
    suffix: null | string;
    summaryGradingGranularity: string;
    summaryGradingType: string;
    missingCalculatorOption?: string;
    yPrefix?: string;
    ySuffix?: string;
    defaultGranularity?: string;
    defaultGranularityType?: string;
}

export interface IChartModuleSeries {
    dates: Array<string>;
    isForDarkMode?: boolean;
    numberToGet?: number | null;
    missingCalculatorOption?: string;
    series: {
        country: string | null;
        optionsId: string | null;
        seriesId: string | null;
        isPlant?: boolean;
        multiplier?: string | null;
        chartType?: string | null;
        colour?: string | null;
        opacity?: number | null;
        dashStyle?: string | null;
        aggregateType?: aggregateType;
        name?: string;
        calculatorIdArray?: Array<string> | null;
        fromToOverride?: null | IDateRangeCompositeNumbers;
        yAxisUnit?: number | null;
        optionsIdArray?: Array<string>;
        plantsForGroup?: Array<string>;
    };
}

export interface IExtraChartInfo {
    currentDisplayOption?: ViewMode;
    currentTimeLine?: '1min' | '5min' | 'period';
    showTime?: boolean;
    showLegend?: boolean;
    yAxisLimits?: IAxisLimits[];
    aggregateOptionsProp?: ICombinedAggregateOptions;
}

export interface IChartModule {
    id: string;
    name: string;
    chartSeries: Array<IChartModuleSeries>;
    date: string | IDateRangeByNumberWithType | IDateObject;
    extraChartInfo?: IExtraChartInfo;
    additionalDetails?: Record<string, IChartSeriesAdditionalDetails>;
    axisPositions?: Array<IAxisPosition> | null;
    axisLeftOrRight?: Array<{ label: string; value: 'left' | 'right' }> | null;
    axisSidePositions?: Array<string> | null;
    nameSetByUser?: boolean;
    legendHeight?: ModuleSize;
    sizeAndPosition?: ISizePosition;
    size?: string;
    reportMeta?: IReportMeta;
}

export interface IGeneralModule {
    info: {
        id: string;
        type: ModuleType;
        sizeAndPosition: ISizePosition;
    };
    data?: {
        date?: IDateObject;
    };
    reportMeta?: IReportMeta;
}

type ModulesWithInfo = INoteModule | IBoaDashboardModule | ILiveTableModule | IForecastHistoryModule | IAncillaryModule | IBoaLeaderboardModule | IAncillaryChartModule | IGeneralModule | IReportPdfPageModule | IIndexGroupModule | ILeaderboardModule | IStorageForecastChartModule;

type ModulesWithoutInfo = IMetricModule | IChartModule;

export function isModuleWithInfoProps (module: IModule): module is ModulesWithInfo {
    return 'info' in module && 'id' in module.info && 'type' in module.info;
}

export function isModuleWithoutInfoProps (module: IModule): module is ModulesWithoutInfo {
    return 'id' in module && 'sizeAndPosition' in module;
}

export function isForecastHistoryModule (module: IModule): module is IForecastHistoryModule {
    return 'info' in module && module.info.type === ModuleType.ForecastHistory;
}

export type IModule =
    IChartModule
    | IForecastHistoryModule
    | ILiveTableModule
    | IBoaDashboardModule
    | IMetricModule
    | INoteModule
    | IAncillaryModule
    | IBoaLeaderboardModule
    | IAncillaryChartModule
    | IGeneralModule
    | IReportPdfPageModule
    | IIndexGroupModule
    | ILeaderboardModule
    | IStorageForecastChartModule;
